import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Search, Users, Loader2, ArrowLeft, TrendingUp,
  Clock, Repeat, Star, ChevronRight, Receipt, ShoppingCart, DollarSign,
  CalendarDays, Armchair, Award, Users as UsersIcon, BarChart3,
} from "lucide-react";
import { cliente360Api, audienciasApi, reservasAdminApi, Cliente360Item } from "../../lib/api";
import { useInicio } from "../../lib/permisos";
import { KpisAvanzadosPanel } from "./KpisAvanzadosPanel";
import { Crown } from "lucide-react";

const DIAS_SEMANA = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
const MESES_LBL = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

type Orden = "ultima_visita" | "total_gastado" | "total_visitas";

function formatNumero(n: number | string): string {
  const num = typeof n === "string" ? Number(n) : n;
  if (isNaN(num)) return "—";
  return new Intl.NumberFormat("es-VE", { maximumFractionDigits: 0 }).format(num);
}

function formatUSD(n: string | number): string {
  const num = typeof n === "string" ? Number(n) : n;
  if (isNaN(num)) return "—";
  return new Intl.NumberFormat("es-VE", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(num);
}

// Las fechas que manda el backend (ultimaVisita, etc.) son fechas puras
// (sin hora) serializadas como instante UTC medianoche -- parsearlas con
// `new Date(d)` directo las corre un día atrás en cualquier navegador con
// huso horario negativo (ej. Venezuela, UTC-4). Se interpretan como
// medianoche LOCAL en vez de UTC para que el día calendario no se mueva.
function parseFechaLocal(d: string): Date {
  return new Date(d.slice(0, 10) + "T00:00:00");
}

function formatFecha(d?: string | null): string {
  if (!d) return "—";
  return parseFechaLocal(d).toLocaleDateString("es-ES", {
    year: "numeric", month: "short", day: "2-digit",
  });
}

function diasDesde(d?: string | null): string {
  if (!d) return "";
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  const dias = Math.round((hoy.getTime() - parseFechaLocal(d).getTime()) / 86400000);
  if (dias === 0) return "hoy";
  if (dias === 1) return "ayer";
  if (dias < 7) return `hace ${dias} días`;
  if (dias < 30) return `hace ${Math.floor(dias / 7)} sem`;
  if (dias < 365) return `hace ${Math.floor(dias / 30)} meses`;
  return `hace ${Math.floor(dias / 365)} años`;
}

// ────────────────────────────────────────────────────────────
// COMPONENTE PRINCIPAL
// ────────────────────────────────────────────────────────────
export function Cliente360ListPage() {
  const inicio = useInicio();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [orden, setOrden] = useState<Orden>("ultima_visita");
  const [page, setPage] = useState(0);
  const [sucursalId, setSucursalId] = useState<number | null>(null);

  const sucursalesDisp = useQuery({
    queryKey: ["cliente360", "sucursales-disponibles"],
    queryFn: () => reservasAdminApi.sucursales(),
  });
  const [sel, setSel] = useState<Set<number>>(new Set());
  const toggleSel = (cod: number) => setSel((s) => {
    const n = new Set(s);
    if (n.has(cod)) { n.delete(cod); } else { n.add(cod); }
    return n;
  });
  async function guardarSeleccion() {
    const cod_clientes = [...sel];
    if (!cod_clientes.length) return;
    const nombre = window.prompt(`Nombre para la audiencia de ${cod_clientes.length} clientes:`, "Selección manual");
    if (nombre === null) return;
    await audienciasApi.crear({ nombre: nombre.trim() || "Selección manual", tipo: "fija", cod_clientes });
    setSel(new Set());
    window.alert("Audiencia creada.");
  }
  const limit = 25;

  // Debounce busqueda
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  // Stats + Dashboard
  const stats = useQuery({
    queryKey: ["cliente360", "stats", sucursalId],
    queryFn: () => cliente360Api.stats(sucursalId ?? undefined),
  });

  const [verDashboard, setVerDashboard] = useState(false);
  const [verKpis, setVerKpis] = useState(false);
  const dashboard = useQuery({
    queryKey: ["cliente360", "dashboard", sucursalId],
    queryFn: () => cliente360Api.dashboard({ sucursalId: sucursalId ?? undefined }),
    enabled: verDashboard,
  });

  // Lista
  const list = useQuery({
    queryKey: ["cliente360", "list", orden, page, debouncedSearch, sucursalId],
    queryFn: () => cliente360Api.list({
      orden, limit, offset: page * limit, busqueda: debouncedSearch || undefined,
      sucursalId: sucursalId ?? undefined,
    }),
  });

  function changeOrden(o: Orden) {
    setOrden(o);
    setPage(0);
  }

  return (
    <div className="min-h-screen bg-ink-50">
      <div className="mx-auto max-w-7xl px-6 py-8 animate-fade-in">

        {/* Barra superior */}
        <div className="flex items-center justify-between mb-5">
          <Link to={inicio} className="inline-flex items-center gap-1.5 text-sm text-ink-500 hover:text-ink-800 transition-colors">
            <ArrowLeft className="size-4" />
            Dashboard
          </Link>
          {(sucursalesDisp.data?.length ?? 0) > 1 && (
            <select
              value={sucursalId ?? ""}
              onChange={(e) => setSucursalId(e.target.value ? Number(e.target.value) : null)}
              className="input text-sm w-auto"
            >
              <option value="">Todas las sucursales</option>
              {sucursalesDisp.data!.map((s) => (
                <option key={s.id} value={s.id}>{s.nombre}</option>
              ))}
            </select>
          )}
        </div>

        {/* Header */}
        <header className="flex items-end justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="size-12 rounded-xl bg-gradient-to-br from-brand-500 to-brand-700 text-white flex items-center justify-center shadow-sm">
              <Users className="size-6" />
            </div>
            <div>
              <h1 className="font-display text-3xl font-bold text-ink-800 leading-tight">Cliente 360°</h1>
              <p className="text-sm mt-0.5 inline-flex items-center gap-1.5 text-success font-medium">
                <span className="size-1.5 rounded-full bg-success" />
                Datos en vivo desde ICG
              </p>
            </div>
          </div>
          {stats.data && (
            <div className="text-right">
              <div className="text-3xl font-display font-bold text-ink-800 leading-none">
                {formatNumero(stats.data.clientesConCompras)}
              </div>
              <div className="text-xs text-ink-500 mt-1">clientes con historial</div>
            </div>
          )}
        </header>

        {stats.isError && (
          <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
            No se pudieron cargar las estadísticas. Probá recargar la página.
          </div>
        )}

        {/* Stats cards */}
        {stats.data && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            <StatCard
              icon={<Receipt className="size-4 text-brand-600" />}
              label="Ventas totales"
              value={formatNumero(stats.data.ventasTotal)}
            />
            <StatCard
              icon={<ShoppingCart className="size-4 text-brand-600" />}
              label="Líneas vendidas"
              value={formatNumero(stats.data.lineasTotal)}
            />
            <StatCard
              icon={<Star className="size-4 text-brand-600" />}
              label="Artículos distintos"
              value={formatNumero(stats.data.articulosVendidos)}
            />
            <StatCard
              icon={<DollarSign className="size-4 text-brand-600" />}
              label="Total facturado"
              value={formatUSD(stats.data.ventasNeto)}
              valueClass="text-base"
            />
          </div>
        )}

        {/* Toggle Dashboard */}
        <button
          onClick={() => setVerDashboard(v => !v)}
          className="card w-full p-3 mb-2 flex items-center justify-between hover:bg-brand-50/30 transition-colors text-left">
          <div className="flex items-center gap-2">
            <BarChart3 className="size-4 text-brand-600" />
            <span className="font-medium text-ink-800">
              {verDashboard ? "Ocultar" : "Ver"} estadísticas detalladas de todos los clientes
            </span>
          </div>
          <ChevronRight className={`size-4 text-ink-400 transition-transform ${verDashboard ? "rotate-90" : ""}`} />
        </button>

        {/* Toggle KPIs avanzados */}
        <button
          onClick={() => setVerKpis(v => !v)}
          className="card w-full p-3 mb-4 flex items-center justify-between hover:bg-amber-50/30 transition-colors text-left">
          <div className="flex items-center gap-2">
            <Crown className="size-4 text-amber-600" />
            <span className="font-medium text-ink-800">
              {verKpis ? "Ocultar" : "Ver"} KPIs avanzados — ticket por comensal, retención, VIP, en riesgo
            </span>
          </div>
          <ChevronRight className={`size-4 text-ink-400 transition-transform ${verKpis ? "rotate-90" : ""}`} />
        </button>

        {verKpis && (
          <div className="mb-5 animate-fade-in">
            <KpisAvanzadosPanel sucursalId={sucursalId} />
          </div>
        )}

        {/* Dashboard (acordeón) */}
        {verDashboard && dashboard.data && (
          <div className="space-y-3 mb-5 animate-fade-in">
            {/* KPIs */}
            {dashboard.data.kpis && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <StatCard icon={<Receipt className="size-4 text-brand-600" />}
                  label="Visitas (con cliente)" value={formatNumero(dashboard.data.kpis.totalVisitas)} />
                <StatCard icon={<Users className="size-4 text-brand-600" />}
                  label="Clientes únicos" value={formatNumero(dashboard.data.kpis.clientesUnicos)} />
                <StatCard icon={<DollarSign className="size-4 text-brand-600" />}
                  label="Ingreso total" value={formatUSD(dashboard.data.kpis.ingresoTotal)} valueClass="text-base" />
                <StatCard icon={<TrendingUp className="size-4 text-brand-600" />}
                  label="Ticket promedio" value={formatUSD(dashboard.data.kpis.ticketPromedio)} valueClass="text-base" />
                <StatCard icon={<UsersIcon className="size-4 text-brand-600" />}
                  label="Comensales prom." value={String(dashboard.data.kpis.comensalesPromedio)} />
              </div>
            )}

            {/* Tendencia mensual */}
            {dashboard.data.tendenciaMensual.length > 0 && (
              <TendenciaChart data={dashboard.data.tendenciaMensual} />
            )}

            {/* 3 charts en fila: hora, día, mes */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <MiniChart titulo="Hora pico" icon={<Clock className="size-4 text-brand-600" />}
                data={dashboard.data.porHora.map(h => ({ label: `${h.hora}h`, valor: h.visitas, key: h.hora }))}
                labelPico={it => `${it.label.replace("h","")}:00`} />
              <MiniChart titulo="Día de semana" icon={<CalendarDays className="size-4 text-brand-600" />}
                data={dashboard.data.porDiaSemana.map((d, i) => ({ label: DIAS_SEMANA[i] || "", valor: d.visitas, key: d.dia }))}
                labelPico={it => it.label} />
              <MiniChart titulo="Mes" icon={<CalendarDays className="size-4 text-brand-600" />}
                data={dashboard.data.porMes.map(m => ({ label: MESES_LBL[m.mes - 1], valor: m.visitas, key: m.mes }))}
                labelPico={it => it.label} />
            </div>

            {/* Top 20 productos vendidos + zonas + comensales */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <div className="card overflow-hidden">
                <div className="flex items-center gap-2 px-4 py-2.5 border-b border-ink-200">
                  <Award className="size-4 text-brand-600" />
                  <h3 className="font-medium text-sm text-ink-800">Top 20 productos vendidos</h3>
                </div>
                <div className="divide-y divide-ink-100 max-h-[480px] overflow-y-auto">
                  {dashboard.data.topProductos.map((p, i) => (
                    <div key={p.codArticulo} className="px-4 py-2 hover:bg-brand-50/30 flex items-center gap-3">
                      <div className={`size-6 rounded shrink-0 flex items-center justify-center text-xs font-bold ${
                        i === 0 ? "bg-amber-100 text-amber-700" :
                        i === 1 ? "bg-ink-200 text-ink-600" :
                        i === 2 ? "bg-orange-100 text-orange-700" :
                        "bg-ink-100 text-ink-500"
                      }`}>{i + 1}</div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-ink-800 truncate">{p.nombreArticulo}</div>
                        <div className="text-xs text-ink-500">
                          {formatNumero(p.vecesPedido)}× pedido · {p.totalUnidades} und · {formatUSD(p.totalGastado)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <MiniBarsH titulo="Zonas más visitadas" icon={<Armchair className="size-4 text-brand-600" />}
                  data={dashboard.data.porZona.map(z => ({
                    label: z.salaNombre || `Sala ${z.salaIcg}`, valor: z.visitas, key: z.salaIcg,
                  }))} />
                <MiniBarsH titulo="Comensales por visita" icon={<UsersIcon className="size-4 text-brand-600" />}
                  data={dashboard.data.porComensales.map(c => ({
                    label: `${c.numComensales} pers.`, valor: c.visitas, key: c.numComensales,
                  }))} />
              </div>
            </div>
          </div>
        )}

        {verDashboard && dashboard.isLoading && (
          <div className="card p-8 mb-5 text-center text-ink-400">
            <Loader2 className="size-5 animate-spin mx-auto mb-2" />
            Calculando estadísticas globales…
          </div>
        )}

        {/* Buscador + ordenamiento */}
        <div className="card p-4 mb-4">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-ink-400" />
              <input
                type="text"
                placeholder="Buscar por nombre, móvil, teléfono o código…"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                className="input pl-10 w-full"
              />
            </div>
            <div className="flex gap-1 bg-ink-100 p-1 rounded-lg">
              <OrdenBtn current={orden} value="ultima_visita" onClick={changeOrden} icon={<Clock className="size-3.5" />}>
                Reciente
              </OrdenBtn>
              <OrdenBtn current={orden} value="total_gastado" onClick={changeOrden} icon={<TrendingUp className="size-3.5" />}>
                Top gasto
              </OrdenBtn>
              <OrdenBtn current={orden} value="total_visitas" onClick={changeOrden} icon={<Repeat className="size-3.5" />}>
                Top visitas
              </OrdenBtn>
            </div>
          </div>
        </div>

        {/* Tabla */}
        <div className="card overflow-hidden">
          {list.isLoading ? (
            <div className="p-16 flex items-center justify-center text-ink-400">
              <Loader2 className="size-6 animate-spin mr-2" />
              Cargando clientes…
            </div>
          ) : list.isError ? (
            <div className="p-8 text-danger">Error: {(list.error as Error).message}</div>
          ) : !list.data || list.data.items.length === 0 ? (
            <div className="p-16 text-center text-ink-400">
              <Users className="size-12 mx-auto mb-2 opacity-30" />
              <p className="font-medium text-ink-600">Sin clientes que coincidan</p>
              <p className="text-xs text-ink-400 mt-2 max-w-xs mx-auto leading-relaxed">
                Probá con teléfono, código de cliente, o parte del nombre.
                Si el cliente es nuevo, aparecerá tras su primera visita.
              </p>
            </div>
          ) : (
            <>
              {sel.size > 0 && (
                <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-brand-50 border-b border-brand-200">
                  <span className="text-sm text-brand-800 font-medium">{sel.size} seleccionados</span>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setSel(new Set())} className="text-xs text-ink-500 hover:text-ink-800">Limpiar</button>
                    <button onClick={guardarSeleccion}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-700">
                      Guardar selección como audiencia
                    </button>
                  </div>
                </div>
              )}
              <div className="overflow-x-auto max-h-[calc(100vh-280px)] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-ink-50 border-b border-ink-200 sticky top-0 z-10 shadow-[0_1px_0_rgba(0,0,0,0.04)]">
                    <tr className="text-ink-600 text-xs uppercase tracking-wide">
                      <th className="px-3 py-3 font-medium text-center w-10">Sel.</th>
                      <th className="px-4 py-3 font-medium text-left">Cliente</th>
                      <th className="px-4 py-3 font-medium text-left">Contacto</th>
                      <th className="px-4 py-3 font-medium text-right">Visitas</th>
                      <th className="px-4 py-3 font-medium text-right">Total gastado</th>
                      <th className="px-4 py-3 font-medium text-right">Ticket promedio</th>
                      <th className="px-4 py-3 font-medium text-left">Última visita</th>
                    </tr>
                  </thead>
                  <tbody className="[&>tr:nth-child(even)]:bg-ink-50/40">
                    {list.data.items.map((c) => (
                      <ClienteRow key={c.codCliente} c={c} seleccionado={sel.has(c.codCliente)} onToggle={() => toggleSel(c.codCliente)} />
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center justify-between px-4 py-3 bg-ink-50 border-t border-ink-200 sticky bottom-0">
                <span className="text-xs text-ink-500">
                  Mostrando <strong className="text-ink-700">{page * limit + 1}-{Math.min((page + 1) * limit, list.data.total)}</strong> de{" "}
                  <strong className="text-ink-700">{formatNumero(list.data.total)}</strong>
                  {list.data.total > limit && (
                    <span className="text-ink-400 ml-1">· página {page + 1} de {Math.ceil(list.data.total / limit)}</span>
                  )}
                </span>
                <div className="flex gap-2">
                  <button onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0}
                    className="btn-secondary disabled:opacity-50">
                    Anterior
                  </button>
                  <button onClick={() => setPage((p) => p + 1)}
                    disabled={(page + 1) * limit >= list.data.total}
                    className="btn-secondary disabled:opacity-50">
                    Siguiente
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// SUB-COMPONENTES
// ────────────────────────────────────────────────────────────
function StatCard({ icon, label, value, valueClass = "text-xl" }: {
  icon: React.ReactNode; label: string; value: string; valueClass?: string;
}) {
  return (
    <div className="card p-3">
      <div className="flex items-center gap-2 text-xs text-ink-500 mb-1">
        {icon}{label}
      </div>
      <div className={`font-display font-bold text-ink-800 ${valueClass}`}>{value}</div>
    </div>
  );
}

function OrdenBtn({ current, value, onClick, icon, children }: {
  current: Orden; value: Orden; onClick: (v: Orden) => void;
  icon: React.ReactNode; children: React.ReactNode;
}) {
  const active = current === value;
  return (
    <button
      onClick={() => onClick(value)}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all
        ${active ? "bg-white shadow-sm text-brand-700" : "text-ink-600 hover:text-ink-800"}`}
    >
      {icon}{children}
    </button>
  );
}

// ════════════════════════════════════════════════════════════
// Mini chart de barras verticales (para hora/día/mes)
// ════════════════════════════════════════════════════════════
type ItemKV = { label: string; valor: number; key: number | string };

function MiniChart({ titulo, icon, data, labelPico }: {
  titulo: string; icon: React.ReactNode; data: ItemKV[]; labelPico: (it: ItemKV) => string;
}) {
  const max = Math.max(...data.map(d => d.valor), 1);
  const pico = data.reduce((a, d) => d.valor > a.valor ? d : a, data[0]);
  const total = data.reduce((s, d) => s + d.valor, 0);
  const MAX_PX = 100;
  return (
    <div className="card overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-ink-200">
        {icon}<h3 className="font-medium text-sm text-ink-800">{titulo}</h3>
        {total > 0 && pico && pico.valor > 0 && (
          <span className="text-xs text-ink-400 ml-auto">
            Pico: <strong className="text-brand-600">{labelPico(pico)}</strong>
          </span>
        )}
      </div>
      <div className="p-3">
        <div className="flex items-end justify-between gap-0.5" style={{ height: MAX_PX + 8 }}>
          {data.map(d => {
            const h = d.valor > 0 ? Math.max(Math.round((d.valor / max) * MAX_PX), 4) : 1;
            const isPeak = d.key === pico?.key && d.valor > 0;
            return (
              <div key={String(d.key)} className="flex-1 group relative">
                {d.valor > 0 && (
                  <div className="absolute -top-7 left-1/2 -translate-x-1/2 bg-ink-800 text-white text-xs px-2 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10 pointer-events-none">
                    {d.valor.toLocaleString("es-ES")}
                  </div>
                )}
                <div className={`w-full rounded-t ${isPeak ? "bg-brand-600" : d.valor > 0 ? "bg-brand-300" : "bg-ink-100"}`}
                  style={{ height: `${h}px` }} />
              </div>
            );
          })}
        </div>
        <div className="flex justify-between text-[10px] text-ink-400 mt-1">
          {data.length <= 12
            ? data.map((d, i) => <span key={i} className="flex-1 text-center">{d.label}</span>)
            : data.map((d, i) => <span key={i} className="flex-1 text-center">{i % 4 === 0 ? d.label : ""}</span>)
          }
        </div>
      </div>
    </div>
  );
}

function MiniBarsH({ titulo, icon, data }: {
  titulo: string; icon: React.ReactNode; data: ItemKV[];
}) {
  const max = Math.max(...data.map(d => d.valor), 1);
  const pico = data.reduce((a, d) => !a || d.valor > a.valor ? d : a, data[0]);
  return (
    <div className="card overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-ink-200">
        {icon}<h3 className="font-medium text-sm text-ink-800">{titulo}</h3>
        {pico && <span className="text-xs text-ink-400 ml-auto">Top: <strong className="text-brand-600">{pico.label}</strong></span>}
      </div>
      <div className="p-3 space-y-1.5">
        {data.length === 0 ? (
          <div className="py-4 text-center text-ink-400 text-xs">Sin datos</div>
        ) : data.slice(0, 8).map(d => {
          const pct = (d.valor / max) * 100;
          const isMax = d.key === pico?.key;
          return (
            <div key={String(d.key)} className="flex items-center gap-2">
              <div className="text-xs font-medium text-ink-700 w-24 truncate shrink-0">{d.label}</div>
              <div className="flex-1 relative h-5 bg-ink-50 rounded overflow-hidden">
                <div className={`absolute inset-y-0 left-0 ${isMax ? "bg-brand-600" : "bg-brand-300"} rounded`} style={{ width: `${pct}%` }} />
                <div className="absolute inset-0 flex items-center px-2 text-xs font-medium">
                  <span className={isMax ? "text-white" : "text-ink-700"}>{d.valor.toLocaleString("es-ES")}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TendenciaChart({ data }: {
  data: Array<{ mes: string; visitas: number; clientes: number; ingreso: string }>
}) {
  const maxV = Math.max(...data.map(d => d.visitas), 1);
  const maxI = Math.max(...data.map(d => Number(d.ingreso) || 0), 1);
  const totalV = data.reduce((s, d) => s + d.visitas, 0);
  const totalI = data.reduce((s, d) => s + (Number(d.ingreso) || 0), 0);
  return (
    <div className="card overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-ink-200">
        <TrendingUp className="size-4 text-brand-600" />
        <h3 className="font-medium text-sm text-ink-800">Tendencia mensual (últimos 12 meses)</h3>
        <span className="text-xs text-ink-400 ml-auto">
          Total: <strong className="text-brand-600">{totalV.toLocaleString("es-ES")} visitas</strong>
          {" · "}<strong className="text-brand-600">{formatUSD(totalI)}</strong>
        </span>
      </div>
      <div className="p-3">
        <div className="flex items-end justify-between gap-1" style={{ height: 110 }}>
          {data.map(d => {
            const hV = Math.max(Math.round((d.visitas / maxV) * 100), 4);
            const hI = Math.max(Math.round(((Number(d.ingreso) || 0) / maxI) * 100), 4);
            return (
              <div key={d.mes} className="flex-1 flex items-end gap-0.5 group relative">
                <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-ink-800 text-white text-xs px-2 py-0.5 rounded opacity-0 group-hover:opacity-100 whitespace-nowrap z-10 pointer-events-none">
                  {d.visitas} visitas · {formatUSD(d.ingreso)}
                </div>
                <div className="flex-1 bg-brand-300 rounded-t" style={{ height: `${hV}px` }} />
                <div className="flex-1 bg-success rounded-t" style={{ height: `${hI}px` }} />
              </div>
            );
          })}
        </div>
        <div className="flex justify-between text-[10px] text-ink-400 mt-1">
          {data.map(d => <span key={d.mes} className="flex-1 text-center">{d.mes.substring(5)}</span>)}
        </div>
        <div className="flex gap-4 text-[10px] text-ink-500 mt-2 justify-center">
          <span className="flex items-center gap-1"><span className="size-2 rounded bg-brand-300 inline-block" /> Visitas</span>
          <span className="flex items-center gap-1"><span className="size-2 rounded bg-success inline-block" /> Ingreso</span>
        </div>
      </div>
    </div>
  );
}

function ClienteRow({ c, seleccionado, onToggle }: { c: Cliente360Item; seleccionado: boolean; onToggle: () => void }) {
  const nombre = c.nombre?.trim() || `Cliente #${c.codCliente}`;
  return (
    <tr className="border-b border-ink-100 hover:bg-brand-50/30 transition-colors">
      <td className="px-3 py-3 text-center">
        <input type="checkbox" checked={seleccionado} onChange={onToggle} aria-label={`Seleccionar ${nombre}`} />
      </td>
      <td className="px-4 py-3">
        <Link to={`/cliente360/${c.codCliente}`} className="block">
          <div className="font-medium text-ink-800 hover:text-brand-600">{nombre}</div>
          <div className="text-xs text-ink-500 font-mono">#{c.codCliente}</div>
        </Link>
      </td>
      <td className="px-4 py-3 text-xs">
        <div className="text-ink-700">{c.mobil || c.telefono1 || "—"}</div>
        <div className="text-ink-500">{c.email || "—"}</div>
      </td>
      <td className="px-4 py-3 text-right font-mono text-ink-800">{c.totalVisitas}</td>
      <td className="px-4 py-3 text-right font-mono text-ink-800 font-medium">
        {formatUSD(c.totalGastado)}
      </td>
      <td className="px-4 py-3 text-right font-mono text-ink-600 text-xs">
        {formatUSD(c.ticketPromedio)}
      </td>
      <td className="px-4 py-3 text-xs">
        <div className="text-ink-700">{formatFecha(c.ultimaVisita)}</div>
        <div className="text-ink-500 italic">{diasDesde(c.ultimaVisita)}</div>
      </td>
    </tr>
  );
}
